import { defineNuxtPlugin } from '#imports'

/**
 * Umami Analytics Tracker — first-party tracking via /_au_/ reverse proxy
 *
 * - .client.ts → browser-only, not SSR (hostname required at runtime)
 * - Loads on production domain only (staging/dev/local are not tracked)
 * - Requires an nginx reverse proxy at `/_au_/` pointing at the Umami VM
 *
 * Tracking endpoints:
 *   /_au_/metrics       → tracker script
 *   /_au_/recorder.js   → replay recorder
 *   /_au_/api/send      → pageview / event ingestion
 *   /_au_/api/record    → replay-data ingestion
 *
 * Disabled tracking (since 2026-05-07):
 *   - Web Vitals performance beacon (LCP/INP/CLS/FCP/TTFB) — switched off via
 *     `data-performance="false"`. Reason: in Umami v3.1 the 10-second performance
 *     beacon carries url+title fields like a pageview and surfaces in the Activity
 *     feed as a duplicate "Viewed page" entry
 *     (see https://github.com/umami-software/umami/issues/4150).
 *     Re-enable once Umami v3.2 ships PR #4228 (`data-auto-pageview`).
 */

interface SiteConfig {
    id: string
    host: string
}

const SITES: Record<string, SiteConfig> = {
    'www.asoundeffect.com': {
        id: '51ef8c98-0360-408f-aacc-58b10555ba67',
        host: 'https://www.asoundeffect.com/_au_',
    },
    'asoundeffect.com': {
        id: '51ef8c98-0360-408f-aacc-58b10555ba67',
        host: 'https://www.asoundeffect.com/_au_',
    },
    // INT / staging — separate Umami *website* (own UUID), so staging traffic can
    // never mix into the live shop's numbers: all Umami data is partitioned by
    // website id. Talks to the analytics host directly instead of a `/_au_`
    // first-party proxy — ad-blocker evasion is pointless on staging, and the
    // analytics host answers `access-control-allow-origin: *` (verified 2026-07-15).
    // Lookup is hostname-keyed, so this entry is inert on production.
    'int.test-env.asoundeffect.com': {
        id: '71c2d842-b1c3-47e7-9ebd-e108faf26c18',
        host: 'https://analytics.audiogroup.com',
    },
}

// Comma-separated allow-list of hostnames the tracker is permitted to send for.
// Defense-in-depth on top of the SITES map above: if this bundle ever gets served
// from a preview / clone domain, the tracker will refuse the call.
const ALLOWED_DOMAINS = 'www.asoundeffect.com,asoundeffect.com,int.test-env.asoundeffect.com'

// Tag attached to every event from this script. Lets us filter "frontend events"
// vs server-side events (e.g. the Phase 3 `purchase` event posted from WordPress)
// in the Umami UI.
const EVENT_TAG = 'frontend'

// Name of the global function used as `data-before-send` hook. Must match the
// attribute set on the script tag below.
const BEFORE_SEND_FN_NAME = '__audiogroupUmamiBeforeSend'

// Allow-list parsed once from ALLOWED_DOMAINS for fast lookups inside the hook.
const ALLOWED_HOSTNAMES = new Set(ALLOWED_DOMAINS.split(',').map(d => d.trim()))

const REPLAY_SAMPLE_RATE = '0.15'   // 15% of sessions
const REPLAY_MASK_LEVEL = 'moderate' // default — masks inputs heuristically
const REPLAY_MAX_DURATION = '300000' // 5 minutes max per recording

/**
 * `data-before-send` hook — drops events with corrupted hostname / url BEFORE
 * they reach Umami's `/api/send`.
 *
 * Why we need this — Umami's tracker patches `history.pushState`/`replaceState`
 * to fire pageviews on URL changes. If any in-page script (third-party widget,
 * browser extension, headless crawler) calls `pushState` with a malformed URL
 * such as `'//null/path'` (template literal with a `null` variable
 * interpolated as the host), the resulting beacon has `hostname: "null"` and
 * pollutes the Activity feed with `https://null/...` entries.
 *
 * The Umami tracker's own `data-domains` check rejects events where the
 * current page's `location.hostname` isn't allow-listed — but it does NOT
 * validate the `payload.hostname` field after pushState mutation. This hook
 * is the second-line defense.
 *
 * Returning `false` aborts the send. The Umami tracker handles that path
 * gracefully (the comparator `e &&` in the source).
 *
 * Symptoms this catches:
 *   - hostname "null" / "undefined" / empty / not in allow-list
 *   - url paths starting with `/<drive-letter>:/` (Windows file:// mirror
 *     opened with a stale tracker — see Backlog "H:/-Pattern")
 */
const beforeSend = (_type: string, payload: Record<string, unknown> | null | undefined) => {
    if (!payload || typeof payload !== 'object') return payload

    const hostname = typeof payload.hostname === 'string' ? payload.hostname : ''
    if (!hostname || hostname === 'null' || hostname === 'undefined') return false
    if (!ALLOWED_HOSTNAMES.has(hostname.toLowerCase())) return false

    // Drop site-mirror URLs (file:///H:/... opened locally with stale tracker)
    const url = typeof payload.url === 'string' ? payload.url : ''
    if (/^\/[A-Z]:\//.test(url) || /:\/\/[^/]+\/[A-Z]:\//.test(url)) return false

    return payload
}

export default defineNuxtPlugin(() => {
    const hostname = window.location.hostname.toLowerCase()
    const config = SITES[hostname]

    if (!config) {
        // staging / DDEV / dev → no tracking
        return
    }

    // Install the before-send hook on `window` BEFORE the (deferred) tracker
    // script loads, so it's resolvable when the tracker dereferences
    // `window[T("before-send")]` on its first send.
    ;(window as Window & Record<string, unknown>)[BEFORE_SEND_FN_NAME] = beforeSend

    // Tracker — pageviews + custom events.
    //
    // data-domains:  defense-in-depth allow-list. The hostname check above is
    //                the first gate; this is the second.
    // data-tag:      separates frontend events from server-side events
    //                (e.g. the Phase 3 server-side `purchase` event posted
    //                from WordPress) so we can filter cleanly in the UI.
    //
    // Recognised data-* attributes in the official Umami v3.1 tracker:
    //   auto-track, before-send, do-not-track, domains, exclude-hash,
    //   exclude-search, fetch-credentials, host-url, performance, tag,
    //   website-id
    // (Verified against the live tracker source at /_au_/metrics, 2026-05-07.)
    const tracker = document.createElement('script')
    tracker.defer = true
    tracker.src = `${config.host}/metrics`
    tracker.setAttribute('data-website-id', config.id)
    tracker.setAttribute('data-host-url', config.host)
    tracker.setAttribute('data-performance', 'false')
    tracker.setAttribute('data-domains', ALLOWED_DOMAINS)
    tracker.setAttribute('data-tag', EVENT_TAG)
    tracker.setAttribute('data-before-send', BEFORE_SEND_FN_NAME)
    document.head.appendChild(tracker)

    // Recorder — session replay (rrweb-based). Shares the website-id with the
    // tracker above on purpose: replay events and analytics events belong to
    // the same Umami site profile.
    const recorder = document.createElement('script')
    recorder.defer = true
    recorder.src = `${config.host}/recorder.js`
    recorder.setAttribute('data-website-id', config.id)
    recorder.setAttribute('data-host-url', config.host)
    recorder.setAttribute('data-sample-rate', REPLAY_SAMPLE_RATE)
    recorder.setAttribute('data-mask-level', REPLAY_MASK_LEVEL)
    recorder.setAttribute('data-max-duration', REPLAY_MAX_DURATION)
    document.head.appendChild(recorder)
})
